/**
 * ChatAttachModal — one "Attach" surface for the chat composer. A pure INPUT
 * surface: drag-and-drop / browse for images + PDFs, and one or more
 * landing-page URL rows (the "+" adds another empty row). Nothing loads here —
 * on "Add to message" the files and URLs are handed to the composer, the modal
 * closes, and the captures load inline in the composer (like a pasted image),
 * where each chip can be clicked to preview / redo / validate.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link2, Plus, UploadCloud, X } from '@digital-twins/design-system/icons'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface ChatAttachModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Raw files from drop or finder — composer compresses / extracts. */
  onFiles: (files: Array<File>) => void
  /** Kick off captures for these URLs (composer owns the loading chips). */
  onAddUrls: (urls: Array<string>) => void
  /** Remaining URL-capture slots (existing captures already count against it). */
  maxUrls: number
  disabled?: boolean
  /**
   * Offer VIDEO files (MP4/WebM/MOV) in the picker. Off by default: only surfaces whose
   * ingest runs with `acceptVideo` (and a hosting uploader) may turn this on, or a picked
   * video is refused downstream. The accept list names the exact containers the ingest
   * gate takes (never a bare video/*), so the picker and the gate cannot disagree.
   */
  acceptVideo?: boolean
}

export function ChatAttachModal({
  open,
  onOpenChange,
  onFiles,
  onAddUrls,
  maxUrls,
  disabled = false,
  acceptVideo = false,
}: ChatAttachModalProps) {
  const { t } = useTranslation(['app', 'common'])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [urls, setUrls] = useState<Array<string>>([''])

  // Start fresh each time the modal opens — don't resurrect rows a previous
  // open left behind without submitting.
  useEffect(() => {
    if (open) setUrls([''])
  }, [open])

  const rowCap = Math.max(1, maxUrls)
  const noSlots = maxUrls <= 0

  const pickFiles = useCallback(() => fileInputRef.current?.click(), [])

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      if (files.length) onFiles(files)
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [onFiles],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const files = Array.from(e.dataTransfer.files ?? [])
      if (files.length) onFiles(files)
    },
    [onFiles],
  )

  const setUrlAt = (i: number, value: string) =>
    setUrls((prev) => prev.map((u, idx) => (idx === i ? value : u)))
  const addRow = () => setUrls((prev) => (prev.length < rowCap ? [...prev, ''] : prev))
  const removeRow = (i: number) =>
    setUrls((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev))

  const submit = useCallback(() => {
    const filled = urls.map((u) => u.trim()).filter(Boolean)
    if (filled.length) onAddUrls(filled)
    setUrls([''])
    onOpenChange(false)
  }, [urls, onAddUrls, onOpenChange])

  const hasUrl = urls.some((u) => u.trim())

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-5">
        <DialogHeader>
          <DialogTitle>{t('chatChrome.addToMessage')}</DialogTitle>
        </DialogHeader>

        {/* Drag-and-drop / click-to-browse */}
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold text-muted-foreground">{t('chatChrome.files')}</p>
          <button
            type="button"
            onClick={pickFiles}
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            disabled={disabled}
            data-testid="chat-attach-dropzone"
            className={cn(
              'flex w-full flex-col items-center justify-center gap-2.5 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors',
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-border bg-muted/30 hover:border-primary/40 hover:bg-muted/50',
              disabled && 'pointer-events-none opacity-50',
            )}
          >
            <UploadCloud
              className={cn(
                'h-8 w-8 transition-colors',
                dragOver ? 'text-primary' : 'text-muted-foreground',
              )}
            />
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                {t('chatChrome.dragDropOr')}{' '}
                <span className="text-primary">{t('chatChrome.browse')}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                {acceptVideo ? t('chatChrome.acceptedTypesVideo') : t('chatChrome.acceptedTypesImage')}
              </p>
            </div>
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptVideo ? 'image/*,video/mp4,video/webm,video/quicktime,.pdf' : 'image/*,.pdf'}
          multiple
          onChange={handleFileInput}
          className="hidden"
          disabled={disabled}
        />

        {/* Landing-page URL rows */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-muted-foreground">{t('chatChrome.landingPagesByUrl')}</p>
            <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
          </div>

          {noSlots ? (
            <p className="text-sm text-muted-foreground">{t('chatChrome.maxPagesReached')}</p>
          ) : (
            <div className="flex flex-col gap-2">
              {urls.map((url, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={url}
                    onChange={(e) => setUrlAt(i, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        submit()
                      }
                    }}
                    placeholder={t('chatChrome.urlPlaceholder')}
                    disabled={disabled}
                    autoFocus={i === 0}
                    data-testid={`chat-attach-url-input-${i}`}
                    className="h-9 flex-1"
                  />
                  {urls.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      aria-label={t('chatChrome.removeUrl')}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}

              {urls.length < rowCap && (
                <button
                  type="button"
                  onClick={addRow}
                  disabled={disabled}
                  data-testid="chat-attach-add-row"
                  className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('chatChrome.addAnotherUrl')}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-center pt-1">
          <Button
            type="button"
            variant="primary"
            onClick={submit}
            disabled={disabled}
            data-testid="chat-attach-done"
            className="min-w-[120px] text-sm"
          >
            {hasUrl ? t('chatChrome.addToMessage') : t('library.done')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
